import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import useCreateTemplateAction, {
  type CreateTemplateAction,
} from "work/proposals/_views/_shared/use-create-template-action";
import { useCreateTeamTemplateMutation } from "work/team-templates/_api/team-template-api";
import TeamTemplate from "work/team-templates/team-template";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import VendorTeamName from "work/work-agreements/teams/vendor-team/vendor-team-name/vendor-team-name";

/** A template the server has made, which therefore has an id. */
export type CreatedTeamTemplate = TeamTemplate & { readonly id: Guid };

export type CreateTeamTemplateActionOptions = {
  /** Who leads the proposal's vendor team. A team template cannot be saved without one. */
  leader: EntityMember | null | undefined;
  /** The vendor team's members as the user sees them, which is who the template saves. */
  members: readonly EntityMember[];
  /** The marketplace team the proposal's team came from, where it came from one. */
  marketplaceId?: Guid;
  /** The proposal's name, which the template's default name is made from. */
  proposalName?: string | null;
  /** Called with the template once the server has made it. */
  onTemplateCreated?: (template: CreatedTeamTemplate) => void;
};

/**
 * Save as Template for a proposal's vendor Team tab: saves the proposal's vendor team as a new team
 * template. The draft and active pages both use it (#5394). The client team has its own,
 * `useCreateClientTeamTemplateAction`, because it saves the other side's roster.
 */
export default function useCreateTeamTemplateAction(
  options: Readonly<CreateTeamTemplateActionOptions>,
): CreateTemplateAction {
  const { leader, members, marketplaceId, proposalName, onTemplateCreated } = options;
  const session = useSession();
  const [createTeamTemplate] = useCreateTeamTemplateMutation();
  const entityId = session.user?.legalEntityId;

  async function handleCreate(name: string): Promise<boolean> {
    if (!leader || !entityId) return false;
    const created = await saveTeamTemplate(createTeamTemplate, {
      entityId,
      name,
      team: new VendorTeam({ leader, members: [...members], marketplaceId }),
      creatorType: session.accountType,
    });
    if (!created) return false;
    onTemplateCreated?.(created);
    return true;
  }

  return useCreateTemplateAction({
    dialogTitle: "Save as Team Template",
    disabledReason: leader && entityId ? undefined : "Select a team leader before saving a template",
    defaultName: getDefaultTeamTemplateName(proposalName),
    onCreate: handleCreate,
  });
}

export function getDefaultTeamTemplateName(proposalName?: string | null): string {
  const trimmedName = proposalName?.trim();
  return trimmedName ? `${trimmedName} Team` : "Proposal Team";
}

/**
 * Sends one create and reports the template it made, or undefined once the user has been told why
 * there is none.
 *
 * At module scope so the hook above stays compilable by the React Compiler. See app/CLAUDE.md.
 */
async function saveTeamTemplate(
  createTeamTemplate: ReturnType<typeof useCreateTeamTemplateMutation>[0],
  request: { entityId: Guid; name: string; team: VendorTeam; creatorType: AccountType },
): Promise<CreatedTeamTemplate | undefined> {
  try {
    const response = await createTeamTemplate({
      entityId: request.entityId.value,
      team: request.team,
      teamName: new VendorTeamName(request.name),
      creatorType: request.creatorType,
    }).unwrap();
    const createdTemplate = TeamTemplate.fromJSON(response);
    if (!hasId(createdTemplate)) throw new Error("Created team template is missing an ID");
    enqueueSnackbar("Team template created", { variant: "success" });
    return createdTemplate;
  } catch (error) {
    console.error("Failed to create team template from proposal", error);
    showErrorSnackbar(error, { doing: "creating the team template", operation: "proposal.create-team-template" });
    return undefined;
  }
}

function hasId(template: TeamTemplate): template is CreatedTeamTemplate {
  return Boolean(template.id);
}
